import { ReportStatus } from "./interfaces/ReportStatus";

// Single source of truth for how a report status is presented to users. The
// report cards, the Status filter dropdown, and the active-filter chips all read
// from here so the three surfaces can never drift apart again.

export type StatusPaletteKey =
  | "warning"
  | "info"
  | "error"
  | "success"
  | "primary";

// Canonical user-facing label for a status. `isCoView` (the viewer holds the
// station-manage permission) only changes the two CO-track statuses whose
// wording differs for a reviewer vs. the authoring officer.
export const reportStatusLabel = (
  status: ReportStatus,
  isCoView = false,
): string => {
  switch (status) {
    case ReportStatus.AWAITING_INPUT:
      return "AWAITING INPUT";
    case ReportStatus.PENDING_REVIEW:
      return "PENDING REVIEW";
    case ReportStatus.GENERATING:
      return "GENERATING";
    case ReportStatus.SIGNED:
      return "SIGNED";
    case ReportStatus.IN_CO_REVIEW:
      return isCoView ? "NEEDS REVIEW" : "SUBMITTED FOR CO REVIEW";
    case ReportStatus.CHANGES_REQUESTED:
      return isCoView ? "REJECTED" : "REJECTED · NEEDS EDITS";
    case ReportStatus.CO_APPROVED:
      return "APPROVED BY CO";
    case ReportStatus.RMS_COMMITTED:
      return "RMS COMMITTED";
    default:
      return status;
  }
};

// Semantic theme-palette key for a status, mirroring the report card's accent
// colors so the filter and the cards speak one color language.
export const reportStatusPalette = (
  status: ReportStatus,
): StatusPaletteKey => {
  switch (status) {
    case ReportStatus.PENDING_REVIEW:
    case ReportStatus.AWAITING_INPUT:
      return "warning";
    case ReportStatus.SIGNED:
    case ReportStatus.IN_CO_REVIEW:
      return "info";
    case ReportStatus.CHANGES_REQUESTED:
      return "error";
    case ReportStatus.CO_APPROVED:
    case ReportStatus.RMS_COMMITTED:
      return "success";
    default:
      return "primary";
  }
};
